import React, { useEffect, useState } from 'react';
import { Users, MessageSquare, Clock, Star, Sparkles, AlertTriangle, Moon } from 'lucide-react';
import { getCommsIntelKpis, getCommsIntelRecommendation } from '../../../data/api';
import { pct } from './ciUtils';

// KPI band + Milo Recommendation hero.
//  • KPI band  ← GET /api/communications/intel/kpis
//  • Recommendation (why + confidence) ← GET /api/communications/intel/recommendation
// Both degrade gracefully: a failed call shows a dash / honest fallback rather
// than crashing the page.

const CONF_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

// ── Inline mini-charts (no chart library) ────────────────────────────────────
// Every chart is derived from the KPI's own real value, so it stays truthful:
// nothing here invents a time series the backend doesn't have.

// A thin progress/fill bar. `ratio` is 0–1; `tone` picks the accent colour.
function MiniProgress({ ratio, tone = 'gold', muted = false }) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(ratio) ? ratio : 0));
  const fill = muted ? 'var(--ci-txt3)'
    : tone === 'green' ? 'var(--ci-green)'
    : tone === 'amber' ? 'var(--ci-gold-hi)'
    : 'var(--ci-gold)';
  return (
    <div className="ci-kpi-spark" role="presentation">
      <div className="ci-kpi-bar">
        <span style={{ width: `${Math.round(clamped * 100)}%`, background: fill }} />
      </div>
    </div>
  );
}

// A compact 4-bar trend built from div columns. `bars` is an array of 0–1
// heights; used where a small distribution reads better than a single bar.
function MiniBars({ bars, tone = 'gold' }) {
  const color = tone === 'green' ? 'var(--ci-green)' : 'var(--ci-gold)';
  return (
    <div className="ci-kpi-spark ci-kpi-bars" role="presentation">
      {bars.map((h, i) => (
        <span key={i} style={{ height: `${Math.max(8, Math.round(Math.max(0, Math.min(1, h)) * 100))}%`, background: color }} />
      ))}
    </div>
  );
}

function KpiCard({ icon, value, sub, label, trend, trendMuted, link, onLink, chart }) {
  // Footer slot is ALWAYS rendered so every card is the same height/structure,
  // whether it carries a trend line, a "View now →" link, or neither. Without
  // this reserved row, cards with no footer (e.g. VIPs at 0, or Avg. Response
  // Time once measurable) collapse shorter than their siblings.
  return (
    <div className="ci-kpi">
      <div className="ci-kpi-top">
        <div className="ci-ic">{icon}</div>
        {chart}
      </div>
      <div className="ci-v">{value}{sub ? <small> {sub}</small> : null}</div>
      <div className="ci-l">{label}</div>
      <div className="ci-kpi-foot">
        {trend ? <span className={`ci-t ${trendMuted ? 'ci-muted' : 'ci-up'}`}>{trend}</span> : null}
        {link ? <span className="ci-link" onClick={onLink}>{link}</span> : null}
      </div>
    </div>
  );
}

function RecHero({ rec, recLoading, onAction, actionState }) {
  if (recLoading) {
    return (
      <div className="ci-rec">
        <div className="ci-tag"><Sparkles size={12} style={{ verticalAlign: -2 }} /> Milo Recommendation</div>
        <div className="ci-loading" style={{ marginTop: 6 }}><span className="ci-spinner" />Thinking…</div>
        <div className="ci-orb"><div className="ci-m">M</div></div>
      </div>
    );
  }
  if (!rec) {
    return (
      <div className="ci-rec">
        <div className="ci-tag"><Sparkles size={12} style={{ verticalAlign: -2 }} /> Milo Recommendation</div>
        <div className="ci-h">No recommendation available</div>
        <div className="ci-when">Milo will surface the next best action once data is available.</div>
        <div className="ci-orb"><div className="ci-m">M</div></div>
      </div>
    );
  }

  const conf = String(rec.confidence || '').toLowerCase();
  const isLow = rec.low_confidence || conf === 'low';
  const confColor = conf === 'high' ? 'var(--ci-green)' : (conf === 'medium' ? 'var(--ci-gold-hi)' : 'var(--ci-gold-hi)');

  // Action states keep the three buttons honest. "approve" + "edit" surface a
  // clearly-labeled pending state because scheduling isn't backed yet.
  if (actionState === 'approved') {
    return (
      <div className="ci-rec">
        <div className="ci-tag"><Sparkles size={12} style={{ verticalAlign: -2 }} /> Milo Recommendation</div>
        <div className="ci-h">{rec.action}</div>
        <div className="ci-when">Ready to schedule — sending isn't wired in this build wave yet.</div>
        <div className="ci-rec-conf" style={{ marginTop: 10 }}>
          <span className="ci-dotc" /><b style={{ color: 'var(--ci-gold-hi)', marginLeft: 2 }}>Queued for the next build wave</b>
        </div>
        <div className="ci-rec-acts">
          <button className="ci-btn ci-ghost ci-sm" onClick={() => onAction('reset')}>Undo</button>
        </div>
        <div className="ci-orb"><div className="ci-m">M</div></div>
      </div>
    );
  }
  if (actionState === 'dismissed') {
    return (
      <div className="ci-rec" style={{ opacity: 0.55 }}>
        <div className="ci-tag"><Sparkles size={12} style={{ verticalAlign: -2 }} /> Milo Recommendation</div>
        <div className="ci-h">Recommendation dismissed</div>
        <div className="ci-when">Milo will resurface this if the underlying signals persist.</div>
        <div className="ci-rec-acts">
          <button className="ci-btn ci-sm" onClick={() => onAction('reset')}>Restore</button>
        </div>
        <div className="ci-orb"><div className="ci-m">M</div></div>
      </div>
    );
  }

  return (
    <div className="ci-rec">
      <div className="ci-tag"><Sparkles size={12} style={{ verticalAlign: -2 }} /> Milo Recommendation</div>
      <div className="ci-h">{rec.action}</div>
      {rec.expected_gain ? <div className="ci-when">{rec.expected_gain}</div> : null}

      {Array.isArray(rec.why) && rec.why.length > 0 && (
        <div className="ci-why">
          <div className="ci-why-h"><Sparkles size={11} /> Why Milo Recommends This</div>
          <ul>
            {rec.why.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div className={`ci-rec-conf ${isLow ? 'ci-low' : ''}`}>
        <span className="ci-dotc" />Milo confidence:
        <b style={{ color: confColor, marginLeft: 2 }}>
          {CONF_LABEL[conf] || 'Low'}
        </b>
      </div>

      {isLow && Array.isArray(rec.missing_data) && rec.missing_data.length > 0 && (
        <div className="ci-lowconf">
          <AlertTriangle size={13} style={{ flex: 'none', marginTop: 1 }} />
          <span>Low confidence — {rec.missing_data.join('; ')}. The recommendation sharpens as more data arrives.</span>
        </div>
      )}

      <div className="ci-rec-acts">
        <button className="ci-btn ci-gold ci-sm" onClick={() => onAction('approve')}>Approve &amp; Schedule</button>
        <button className="ci-btn ci-sm" onClick={() => onAction('edit')}>Edit</button>
        <button className="ci-btn ci-ghost ci-sm" onClick={() => onAction('dismiss')}>Dismiss</button>
      </div>
      <div className="ci-orb"><div className="ci-m">M</div></div>
    </div>
  );
}

// Slim "resting" stand-in for the recommendation hero while Milo is off.
function RestingHero({ onWake }) {
  return (
    <div className="ci-rec ci-rec-rest">
      <div className="ci-tag"><Moon size={12} style={{ verticalAlign: -2 }} /> Milo is resting</div>
      <div className="ci-h">Recommendations paused</div>
      <div className="ci-when">Milo won't surface a next best action until you turn him back on.</div>
      <div className="ci-rec-acts">
        <button className="ci-btn ci-gold ci-sm" onClick={onWake}><Sparkles size={13} /> Turn Milo on</button>
      </div>
      <div className="ci-orb"><div className="ci-m">M</div></div>
    </div>
  );
}

export default function KpiBand({ onViewVips, onEditRecommendation, miloQuiet = false, onWakeMilo }) {
  const [kpis, setKpis] = useState(null);
  const [kpisErr, setKpisErr] = useState(false);
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(true);
  const [actionState, setActionState] = useState('idle');

  useEffect(() => {
    let alive = true;
    getCommsIntelKpis()
      .then((d) => { if (alive) setKpis(d); })
      .catch(() => { if (alive) setKpisErr(true); });
    getCommsIntelRecommendation()
      .then((d) => { if (alive) setRec(d); })
      .catch(() => { if (alive) setRec(null); })
      .finally(() => { if (alive) setRecLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleAction = (a) => {
    if (a === 'approve') setActionState('approved');
    else if (a === 'dismiss') setActionState('dismissed');
    else if (a === 'reset') setActionState('idle');
    else if (a === 'edit' && onEditRecommendation) onEditRecommendation(rec);
  };

  const rr = kpis && kpis.response_rate !== null && kpis.response_rate !== undefined ? pct(kpis.response_rate) : '—';
  const art = kpis && kpis.avg_response_time_hrs !== null && kpis.avg_response_time_hrs !== undefined
    ? String(kpis.avg_response_time_hrs) : '—';

  // ── Mini-chart inputs, all derived from the real KPI values ───────────────
  const raw = kpis && kpis.raw ? kpis.raw : null;
  const ready = kpis && !kpisErr;
  const engagedRatio = raw && raw.total_guests > 0 ? kpis.guests_engaged / raw.total_guests : 0;
  const responseRatio = kpis && kpis.response_rate != null ? Number(kpis.response_rate) : 0;
  // Faster replies read as a fuller bar; benchmarked against a 24h target.
  const speedRatio = kpis && kpis.avg_response_time_hrs != null
    ? Math.max(0, 1 - Number(kpis.avg_response_time_hrs) / 24) : 0;
  const vipTotal = raw && raw.vip_total ? raw.vip_total : 0;
  const vipRatio = vipTotal > 0 ? (kpis.vips_need_attention || 0) / vipTotal : 0;

  return (
    <div className="ci-kpis ci-kpis-hero">
      <KpiCard
        icon={<Users size={15} />}
        value={kpisErr ? '—' : (kpis ? kpis.guests_engaged : '…')}
        label="Guests Engaged"
        trend={kpis && kpis.raw ? `of ${kpis.raw.total_guests} on the list` : null}
        trendMuted
        chart={<MiniProgress ratio={engagedRatio} tone="gold" muted={!ready} />}
      />
      <KpiCard
        icon={<MessageSquare size={15} />}
        value={kpisErr ? '—' : (kpis ? rr.replace('%', '') : '…')}
        sub={kpis && rr !== '—' ? '%' : ''}
        label="Response Rate"
        trend={kpis && kpis.raw ? `${kpis.raw.inbound_messages_handled}/${kpis.raw.inbound_messages_total} handled` : null}
        trendMuted
        chart={<MiniProgress ratio={responseRatio} tone="green" muted={!ready || rr === '—'} />}
      />
      <KpiCard
        icon={<Clock size={15} />}
        value={kpisErr ? '—' : (kpis ? art : '…')}
        sub={kpis && art !== '—' ? 'hrs' : ''}
        label="Avg. Response Time"
        trend={kpis && art === '—' ? 'Not yet measurable' : null}
        trendMuted
        chart={art === '—'
          ? <MiniProgress ratio={0} muted />
          : <MiniBars bars={[speedRatio * 0.7, speedRatio * 0.9, speedRatio * 0.8, speedRatio]} tone="gold" />}
      />
      <KpiCard
        icon={<Star size={15} />}
        value={kpisErr ? '—' : (kpis ? kpis.vips_need_attention : '…')}
        label="VIPs Need Attention"
        link={kpis && kpis.vips_need_attention > 0 ? 'View now →' : null}
        onLink={onViewVips}
        // When there's no "View now →" link (VIPs = 0, or not ready), render a
        // muted trend line instead so the footer carries a full text row exactly
        // like the sibling cards' trend lines — otherwise this card collapses
        // ~10px shorter than its three siblings (empty footer vs a trend row).
        trend={ready && !(kpis && kpis.vips_need_attention > 0) ? 'All VIPs handled' : null}
        trendMuted
        chart={<MiniProgress ratio={vipRatio} tone="amber" muted={!ready || vipTotal === 0} />}
      />
      {miloQuiet
        ? <RestingHero onWake={onWakeMilo} />
        : <RecHero rec={rec} recLoading={recLoading} onAction={handleAction} actionState={actionState} />}
    </div>
  );
}
